import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Clock, Mic, Square, Send, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2, Sparkles, Play, FileText, Lightbulb } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const wordCount = (txt) => (txt || '').trim().split(/\s+/).filter(Boolean).length;
const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const PART_LABEL = { part_1: 'Part 1', part_2: 'Part 2', part_3: 'Part 3' };

const IeltsSpeakingMockPage = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [session, setSession] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null);
  const [activePart, setActivePart] = useState('part_1');
  const [phase, setPhase] = useState('speaking'); // prep | speaking (P2 only)
  const [responses, setResponses] = useState({ part_1: '', part_2: '', part_3: '' });
  const [seconds, setSeconds] = useState({ part_1: 0, part_2: 0, part_3: 0 });
  const [prepSec, setPrepSec] = useState(0);
  const [submittedIds, setSubmittedIds] = useState({});
  const [results, setResults] = useState(null);
  const [working, setWorking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const timerRef = useRef(null);
  const prepRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try { await ieltsApi.authMe(); if (mounted) setAuthChecked(true); }
      catch { if (mounted) { setAuthChecked(true); setNeedsLogin(true); } }
    })();
    return () => { mounted = false; };
  }, []);

  const currentPlan = session?.plan?.[activePart];
  const currentTarget = currentPlan?.duration_seconds || (activePart === 'part_2' ? 120 : 240);
  const currentSec = seconds[activePart] || 0;
  const currentRem = Math.max(0, currentTarget - currentSec);
  const currentWc = useMemo(() => wordCount(responses[activePart]), [responses, activePart]);
  const isPart2 = activePart === 'part_2';
  const allParts = session?.parts || [];

  // Prep timer (P2 only)
  useEffect(() => {
    if (!session || !isPart2 || phase !== 'prep' || results) return;
    prepRef.current = setInterval(() => setPrepSec((s) => s + 1), 1000);
    return () => clearInterval(prepRef.current);
  }, [session, isPart2, phase, results]);
  useEffect(() => {
    if (isPart2 && phase === 'prep' && prepSec >= 60) setPhase('speaking');
  }, [isPart2, phase, prepSec]);

  // Main timer
  useEffect(() => {
    if (!session || !activePrompt || results) return;
    if (isPart2 && phase !== 'speaking') return;
    timerRef.current = setInterval(() => setSeconds((s) => ({ ...s, [activePart]: (s[activePart] || 0) + 1 })), 1000);
    return () => clearInterval(timerRef.current);
  }, [session, activePrompt, results, isPart2, phase, activePart]);

  const start = async () => {
    setWorking(true);
    try {
      const r = await ieltsApi.speakingMockStart({ module: 'shared', parts: ['part_1', 'part_2', 'part_3'] });
      setSession(r);
      const first = r.parts[0];
      setActivePart(first);
      setPhase(first === 'part_2' ? 'prep' : 'speaking');
      const p = await ieltsApi.speakingMockPrompt(r.mock_session_id, r.plan[first].prompt_id);
      setActivePrompt(p.prompt);
    } catch (e) { toast.error(e.message || 'Could not start mock'); }
    finally { setWorking(false); }
  };

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      toast.info('Recording not supported here. Use the transcript field.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) { toast.error('Microphone denied — use transcript field'); }
  };
  const stopRec = () => { try { mediaRecRef.current?.stop(); } catch { /* ignore */ } setRecording(false); };

  const submitCurrent = async () => {
    if (!session || !activePrompt) return;
    setWorking(true);
    try {
      const r = await ieltsApi.speakingSubmitResponse({
        prompt_id: activePrompt.prompt_id,
        response_text: responses[activePart] || '',
        seconds_spent: currentSec,
        has_recording: !!recordedUrl,
        is_mock: true,
        mock_session_id: session.mock_session_id,
        request_ai_feedback: false,
      });
      setSubmittedIds((m) => ({ ...m, [activePart]: r.submission_id }));
      toast.success(`Saved ${PART_LABEL[activePart]} response`);
      // Move to next part
      const idx = allParts.indexOf(activePart);
      if (idx >= 0 && idx < allParts.length - 1) {
        const next = allParts[idx + 1];
        setActivePart(next);
        setPhase(next === 'part_2' ? 'prep' : 'speaking');
        setPrepSec(0);
        setRecordedUrl(null);
        const p = await ieltsApi.speakingMockPrompt(session.mock_session_id, session.plan[next].prompt_id);
        setActivePrompt(p.prompt);
      } else {
        await finalize();
      }
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setWorking(false); }
  };

  const finalize = async () => {
    if (!session) return;
    setWorking(true);
    try {
      const r = await ieltsApi.speakingMockFinalize(session.mock_session_id);
      const subs = r.submissions || [];
      await Promise.all(subs.map((s) => ieltsApi.speakingEvaluateSubmission(s.submission_id).catch(() => null)));
      const updated = await Promise.all(subs.map((s) => ieltsApi.speakingGetSubmission(s.submission_id).catch(() => s)));
      setResults({ ...r, submissions: updated });
    } catch (e) { toast.error(e.message || 'Finalize failed'); }
    finally { setWorking(false); }
  };

  if (!authChecked) return (<div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-red-700" /></div>);
  if (needsLogin) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center bg-white border border-gray-200 rounded-2xl p-8">
            <h1 className="text-2xl font-bold mb-2">Sign in to take the mock</h1>
            <Button onClick={() => goToIeltsAuth('/ielts/speaking/mock')} className="bg-black text-white rounded-full px-6 h-11">Sign in with Google</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ielts-speaking-mock">
      <Seo title="IELTS Speaking Mock · Winhouse" canonical="/ielts/speaking/mock" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 pt-24 sm:pt-28 pb-8 w-full">
        {!session && !results && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-10 max-w-2xl mx-auto" data-testid="speaking-mock-start">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Speaking Mock Test</div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">Full Speaking Mock — ~12 minutes</h1>
            <p className="text-gray-600 mb-5">Three parts back-to-back, just like the real exam.</p>
            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Part 1</div><div className="font-semibold text-sm">~4 min · short questions</div></div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Part 2</div><div className="font-semibold text-sm">1 min prep · 2 min talk</div></div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Part 3</div><div className="font-semibold text-sm">~5 min · discussion</div></div>
            </div>
            <Button onClick={start} disabled={working} className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold rounded-full h-12 px-7 text-base" data-testid="speaking-mock-begin">
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />} Begin Mock Test
            </Button>
          </div>
        )}

        {session && !results && activePrompt && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/ielts/speaking')} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" />Exit</Button>
                <Badge className="bg-black text-white">{PART_LABEL[activePart]}</Badge>
                {allParts.map((k) => (
                  <Badge key={k} variant={submittedIds[k] ? 'default' : 'outline'}
                    className={submittedIds[k] ? 'bg-green-100 text-green-700 border-green-300' : ''}>
                    {submittedIds[k] ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> : null}{PART_LABEL[k]}
                  </Badge>
                ))}
              </div>
              {isPart2 && phase === 'prep' ? (
                <div className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums bg-blue-50 text-blue-700 border border-blue-200 text-sm" data-testid="speaking-mock-prep">
                  Prep · {fmt(60 - prepSec)}
                </div>
              ) : (
                <div className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums text-sm ${
                  currentRem === 0 ? 'bg-red-50 text-red-700 border border-red-200'
                  : currentRem < 30 ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-white text-black border border-gray-200'
                }`} data-testid="speaking-mock-timer">
                  <Clock className="h-4 w-4" /> {fmt(currentRem)}
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 max-h-[calc(100vh-220px)] overflow-auto" data-testid="speaking-mock-prompt-pane">
                <div className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold mb-3">
                  {PART_LABEL[activePart]}{activePrompt.topic ? <span className="ml-2 text-gray-500 normal-case tracking-normal">· {activePrompt.topic.replace(/_/g, ' ')}</span> : null}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-3">{activePrompt.subtopic || (activePrompt.prompt_text || '').split('\n')[0]}</h2>
                {activePrompt.speaking_part === 'part_2' && activePrompt.cue_card_text ? (
                  <div className="bg-[#FAF9F6] border-2 border-dashed border-gray-300 rounded-2xl p-5 sm:p-6">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2 flex items-center gap-1.5"><FileText className="h-3 w-3" /> Cue Card</div>
                    <div className="whitespace-pre-wrap font-serif text-[15px] leading-7">{activePrompt.cue_card_text}</div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-[15px] leading-7 font-serif">{activePrompt.prompt_text}</div>
                )}
                {Array.isArray(activePrompt.follow_up_questions) && activePrompt.follow_up_questions.length > 0 && activePart !== 'part_2' && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Questions</div>
                    <ul className="space-y-1.5 text-sm leading-6">
                      {activePrompt.follow_up_questions.map((q, i) => (<li key={i} className="flex gap-2"><span className="text-red-700">→</span>{q}</li>))}
                    </ul>
                  </div>
                )}
              </section>

              <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 flex flex-col" data-testid="speaking-mock-record-pane">
                {isPart2 && phase === 'prep' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3"><Lightbulb className="h-6 w-6 text-blue-700" /></div>
                    <h3 className="font-bold text-lg mb-1">1 minute preparation</h3>
                    <p className="text-sm text-gray-600 mb-5">Jot down notes. Speaking starts automatically when prep ends.</p>
                    <textarea value={responses.part_2} onChange={(e) => setResponses((m) => ({ ...m, part_2: e.target.value }))}
                      rows={5} placeholder="Notes…"
                      className="w-full border border-gray-200 rounded-xl p-3 text-sm leading-6 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none" />
                    <Button onClick={() => setPhase('speaking')} className="mt-4 bg-black text-white rounded-full px-6 h-11">Skip · Start speaking now <ArrowRight className="h-4 w-4 ml-1" /></Button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-bold text-lg mb-3">Your response</h3>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {!recording ? (
                        <Button onClick={startRec} className="bg-red-700 hover:bg-red-800 text-white rounded-full h-10 px-4 text-sm"><Mic className="h-4 w-4 mr-1.5" />{recordedUrl ? 'Re-record' : 'Record'}</Button>
                      ) : (
                        <Button onClick={stopRec} className="bg-black text-white rounded-full h-10 px-4 text-sm"><Square className="h-4 w-4 mr-1.5" />Stop</Button>
                      )}
                      {recordedUrl && !recording && <audio src={recordedUrl} controls className="h-9" />}
                    </div>
                    <textarea value={responses[activePart]} onChange={(e) => setResponses((m) => ({ ...m, [activePart]: e.target.value }))}
                      rows={10} placeholder="Type or paraphrase what you said so the AI can score it…"
                      className="flex-1 w-full resize-y border border-gray-200 rounded-xl p-4 text-[14px] leading-7 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none"
                      data-testid="speaking-mock-transcript" />
                    <div className="mt-2 text-xs text-gray-500">{currentWc} words</div>
                    <Button onClick={submitCurrent} disabled={working} className="bg-black text-white rounded-full h-11 px-5 mt-3 self-start" data-testid="speaking-mock-submit">
                      {working ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        : activePart === allParts[allParts.length - 1] ? <Send className="h-4 w-4 mr-1.5" />
                        : <ArrowRight className="h-4 w-4 mr-1.5" />}
                      {activePart === allParts[allParts.length - 1] ? 'Finish Mock & Get Feedback' : `Save & Continue to ${PART_LABEL[allParts[allParts.indexOf(activePart) + 1]]}`}
                    </Button>
                  </>
                )}
              </section>
            </div>
          </>
        )}

        {results && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8" data-testid="speaking-mock-results">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-red-700" /><h2 className="text-2xl font-bold">Speaking Mock Complete</h2></div>
              <WinhouseAIFeedbackBadge model="claude-sonnet-4-5" />
            </div>
            <p className="text-gray-600 mb-6">Average speaking band across the three parts: <span className="font-bold text-black text-lg">{results.average_band ?? '—'}</span></p>
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              {(results.submissions || []).map((s) => {
                const ai = s.ai_feedback || {}; const bands = ai.bands || {};
                return (
                  <div key={s.submission_id} className="border border-gray-200 rounded-2xl p-4">
                    <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-1">{PART_LABEL[s.speaking_part]} · {s.topic}</div>
                    <div className="flex items-baseline gap-2 mb-2"><span className="text-3xl font-bold">{s.estimated_band_overall ?? '—'}</span><span className="text-xs text-gray-500 uppercase tracking-wider">band</span></div>
                    <div className="grid grid-cols-2 gap-1.5 text-xs mb-2">
                      <div className="bg-gray-50 border rounded p-2">FC<div className="text-sm font-bold">{bands.fluency_coherence ?? '—'}</div></div>
                      <div className="bg-gray-50 border rounded p-2">LR<div className="text-sm font-bold">{bands.lexical_resource ?? '—'}</div></div>
                      <div className="bg-gray-50 border rounded p-2">GRA<div className="text-sm font-bold">{bands.grammatical_range_accuracy ?? '—'}</div></div>
                      <div className="bg-gray-50 border rounded p-2">Pron<div className="text-sm font-bold">{bands.pronunciation ?? '—'}</div></div>
                    </div>
                    {ai.summary && <p className="text-xs text-gray-600 leading-5">{ai.summary.slice(0, 200)}</p>}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/ielts/speaking/dashboard')} className="bg-black text-white rounded-full px-6">Open Dashboard</Button>
              <Button variant="outline" onClick={() => { setSession(null); setResults(null); setResponses({part_1:'',part_2:'',part_3:''}); setSeconds({part_1:0,part_2:0,part_3:0}); setSubmittedIds({}); }} className="rounded-full px-6">Try another mock</Button>
            </div>
            <WinhouseAIFeedbackBadge variant="footer" model="claude-sonnet-4-5" />
          </div>
        )}
      </main>
    </div>
  );
};
export default IeltsSpeakingMockPage;
